// @givmo/org-console — the settings page's ORIGIN, carried in its ADDRESS (Org Machinery OM-28).
//
// The gear's page is composed for the module it was opened from and wears that page's sidebar, so
// it has to know where the gear was pressed (D-CPO-33). Until OM-28 that rode history state alone,
// which a reload keeps but a NEW TAB does not: the gear or the sidebar's Settings row opened with a
// modifier, a bookmark or a shared link reached `/settings` with no origin, and the module's section
// was gone (Board is the first module with a section). The address now carries the origin too —
// `/settings?from=<the pathname the gear was pressed on>&frame=<the frame that page wore>` — so the
// same section and the same sidebar render however the page is reached. The sidebar's frame rule
// reads these two values as it read history state — and, since an address can say anything, counts
// them only where they give the person a place to stand (`sidebar.ts` `originGivesAPlace`, the
// OM-28 review fold).
//
// Built once here; each host's `settings-origin.ts` binds it to its router (the route's
// `validateSearch`, the gear's navigate and its `href`). A value the reader does not trust is
// dropped, never an error: the page then falls to the cold-load rule, as a bare `/settings` does.

import { sanitizeRedirectTarget } from './redirect-target';

/** The address's key for the origin's pathname. */
export const SETTINGS_ORIGIN_SEARCH_KEY = 'from';
/** The address's key for the frame the origin wore (a module slug; absent when it wore none). */
export const SETTINGS_FRAME_SEARCH_KEY = 'frame';

/** The settings route's search — both keys optional; a bare `/settings` is the cold load. */
export interface SettingsOriginSearch {
  from?: string;
  frame?: string;
}

/** Long enough for any console pathname; a longer value is not one this console wrote. */
const MAX_ORIGIN_LENGTH = 512;
/** A module slug as the console tables spell them (`donations`, `donors`, `board`). */
const FRAME_SLUG = /^[a-z][a-z0-9_-]{0,63}$/;

/**
 * An origin the page may frame itself by: a path in this console, as the package's ONE answer to
 * that question reads it (`sanitizeRedirectTarget` — a leading slash, never `//host` nor `/\host`,
 * which browsers read as off-site) and no longer than a console path — or `null`. It is the
 * router's DECODED pathname, so it may hold whatever its segments decoded to (a space, a `?`):
 * inside the query it is encoded, and it is only ever matched against the console's own paths,
 * never navigated to.
 */
export function settingsOriginFromAddress(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > MAX_ORIGIN_LENGTH) return null;
  return sanitizeRedirectTarget(value) ?? null;
}

/** The frame an address names — a module slug — or `undefined`. */
export function settingsFrameFromAddress(value: unknown): string | undefined {
  return typeof value === 'string' && FRAME_SLUG.test(value) ? value : undefined;
}

/**
 * The settings route's `validateSearch`: keeps a trusted origin and, beside it, a trusted frame —
 * a frame qualifies the origin it was worn on, so alone it means nothing — and drops anything else.
 */
export function parseSettingsOriginSearch(search: Record<string, unknown>): SettingsOriginSearch {
  const from = settingsOriginFromAddress(search[SETTINGS_ORIGIN_SEARCH_KEY]);
  if (from === null) return {};
  const frame = settingsFrameFromAddress(search[SETTINGS_FRAME_SEARCH_KEY]);
  return frame === undefined ? { from } : { from, frame };
}

/**
 * The search the gear's navigate carries: the pathname it was pressed on and, when that page wore
 * one, its frame. `null` (the Admin Console family, the floor) and an untrusted value carry nothing.
 */
export function settingsOriginSearchFor(
  from: string,
  frame: string | null | undefined,
): SettingsOriginSearch {
  return parseSettingsOriginSearch({
    [SETTINGS_ORIGIN_SEARCH_KEY]: from,
    [SETTINGS_FRAME_SEARCH_KEY]: frame ?? undefined,
  });
}

/**
 * The gear's `href` — the settings route with the origin in its address — so a new tab, a copied
 * link or a bookmark opens the same page the in-app navigate does.
 */
export function settingsOriginHref(
  route: string,
  from: string,
  frame: string | null | undefined,
): string {
  const search = settingsOriginSearchFor(from, frame);
  const params = new URLSearchParams();
  if (search.from !== undefined) params.set(SETTINGS_ORIGIN_SEARCH_KEY, search.from);
  if (search.frame !== undefined) params.set(SETTINGS_FRAME_SEARCH_KEY, search.frame);
  const query = params.toString();
  return query.length === 0 ? route : `${route}?${query}`;
}
